/**
 * Topic client facade — single entry point UI / hooks use for Topic ops.
 *
 * Wraps the real `@amzn/ivthub-client` Smithy commands (Step 2 from the
 * `features/ivt-hub/solution-map-v3-design.md` design — backend now deployed).
 * Returning `null` from list/upsert paths on partial backend data lets the
 * existing UI code keep its non-null narrowing without each call site
 * defensively coalescing — the SDK marks fields like `Topic.name` as
 * `string | undefined` because Smithy `@required` is wire-shape-only.
 *
 * Note: VITE_IVT_MOCK=1 routes `getIvtClient()` to MockIvtHubClient, which
 * is what backs local-only dev (see IvtHubClient.ts `isMockMode`).
 */

import {
  getIvtClient,
  ListTopicsCommand,
  UpsertTopicCommand,
  UpsertTopicInstanceCommand,
  DeleteTopicCommand,
  DeleteTopicInstanceCommand,
  LinkTopicAssetCommand,
  type Topic,
  type TopicAgentInput,
  type TopicInstance,
  type TopicAssetLinkRef,
} from './IvtHubClient';

export interface UpsertTopicInput {
  topicName: string;
  description?: string;
  knowledgeBase?: string;
  sharedTech?: string;
  potential?: string;
  agent?: TopicAgentInput;
}

export interface UpsertTopicInstanceInput {
  topicName: string;
  industry: string;
  subTopicName: string;
  notes?: string;
}

export interface DeleteTopicInstanceInput {
  topicName: string;
  industry: string;
  subTopicName: string;
}

export const topicClient = {
  async list(): Promise<{ topics: Topic[] }> {
    const out = await getIvtClient().send(new ListTopicsCommand({}));
    return { topics: out.items ?? [] };
  },

  async upsert(input: UpsertTopicInput): Promise<{ topic: Topic | undefined }> {
    const out = await getIvtClient().send(new UpsertTopicCommand(input));
    return { topic: out.topic };
  },

  async upsertInstance(
    input: UpsertTopicInstanceInput,
  ): Promise<{ instance: TopicInstance | undefined }> {
    const out = await getIvtClient().send(new UpsertTopicInstanceCommand(input));
    return { instance: out.instance };
  },

  async deleteInstance(input: DeleteTopicInstanceInput): Promise<void> {
    await getIvtClient().send(new DeleteTopicInstanceCommand(input));
  },

  async deleteTopic(input: { topicName: string }): Promise<void> {
    await getIvtClient().send(new DeleteTopicCommand(input));
  },

  /**
   * Link an asset to a sub-topic (topic_instances.id). Idempotent by
   * (subTopicId, assetId). Backs Topic-mode asset creation: the modal first
   * CreateAsset, then linkTopicAsset with the new id + the picked sub-topic id.
   */
  async linkTopicAsset(input: {
    assetId: string;
    subTopicId: number;
  }): Promise<{ link: TopicAssetLinkRef | undefined }> {
    const out = await getIvtClient().send(new LinkTopicAssetCommand(input));
    return { link: out.link };
  },
};
